import { Button, Dropdown, Flex, Tag, Typography } from 'antd'
import type { TableColumnsType } from 'antd'
import { Eye, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DateTimeCell,
  ProTableContainer,
  TableActionColumnTitle,
  UserCell
} from 'src/components'
import { DEFAULT_PER_PAGE } from 'src/hooks'

import type { OrgTypeFilter, OrgUnit } from '../types'
import { KIND_COLOR, apiTypeToKind, childKindOf, tintHex } from '../utils'

import { OrgTypeIcon } from './OrgTypeIcon'

const { Link, Text } = Typography

type Props = {
  units: OrgUnit[]
  search: string
  typeFilter: OrgTypeFilter
  onSelect: (unit: OrgUnit) => void
  /** Omitted when the viewer may not change the structure. */
  onEdit?: (unit: OrgUnit) => void
  onAddChild?: (unit: OrgUnit) => void
  onDelete?: (unit: OrgUnit) => void
}

export const OrgBrowseView = ({
  units,
  search,
  typeFilter,
  onSelect,
  onEdit,
  onAddChild,
  onDelete
}: Props) => {
  const { t } = useTranslation()

  const parentName = useMemo(() => {
    const m = new Map<number, string>()
    for (const u of units) m.set(u.id, u.name)
    return m
  }, [units])

  const rows = useMemo(() => {
    const ql = search.trim().toLowerCase()

    return units.filter((u) => {
      if (typeFilter !== 'all' && apiTypeToKind(u.type) !== typeFilter)
        return false
      if (!ql) return true
      return u.name.toLowerCase().includes(ql)
    })
  }, [units, search, typeFilter])

  const columns: TableColumnsType<OrgUnit> = [
    {
      title: t('organization.browse.columns.name'),
      dataIndex: 'name',
      fixed: 'left',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, row) => {
        const kind = apiTypeToKind(row.type)
        const color = KIND_COLOR[kind]

        return (
          <Flex align="center" gap={8}>
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 5,
                background: tintHex(color),
                color,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12
              }}
            >
              <OrgTypeIcon kind={kind} size={12} />
            </span>
            <Link strong style={{ fontSize: 13 }} onClick={() => onSelect(row)}>
              {row.name}
            </Link>
          </Flex>
        )
      }
    },
    {
      title: t('organization.browse.columns.type'),
      dataIndex: 'type',
      width: 120,
      sorter: (a, b) => (a.type ?? '').localeCompare(b.type ?? ''),
      render: (_, row) => {
        const kind = apiTypeToKind(row.type)
        const color = KIND_COLOR[kind]

        return (
          <Tag
            style={{
              marginInlineEnd: 0,
              background: tintHex(color),
              color,
              border: 'none',
              fontSize: 11
            }}
          >
            {t(`organization.types.${kind}`)}
          </Tag>
        )
      }
    },
    {
      title: t('organization.browse.columns.parent'),
      dataIndex: 'parent',
      width: 200,
      render: (v: OrgUnit['parent']) =>
        v != null ? (
          <Text style={{ fontSize: 12 }}>{parentName.get(v) ?? '—'}</Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('organization.browse.workspaceRoot')}
          </Text>
        )
    },
    {
      title: t('organization.browse.columns.lead'),
      dataIndex: 'head_user_detail',
      width: 240,
      render: (_, row) => {
        const h = row.head_user_detail
        if (!h) {
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('organization.tree.leadVacant')}
            </Text>
          )
        }
        return (
          <UserCell
            firstName={h.first_name}
            lastName={h.last_name}
            email={h.email}
            color={KIND_COLOR[apiTypeToKind(row.type)]}
            size="sm"
            hideMeta
          />
        )
      }
    },
    {
      title: t('organization.browse.columns.headcount'),
      dataIndex: 'users_count',
      width: 130,
      align: 'right' as const,
      sorter: (a, b) => a.users_count - b.users_count,
      render: (v: number) => (
        <Flex align="center" gap={6} justify="flex-end">
          <Text
            strong
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {v}
          </Text>
        </Flex>
      )
    },
    {
      title: t('organization.browse.columns.created'),
      dataIndex: 'date_created',
      width: 130,
      render: (v: string) => <DateTimeCell value={v} style={{ fontSize: 12 }} />
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 56,
      align: 'center' as const,
      render: (_, row) => {
        const kind = apiTypeToKind(row.type)
        const childKind = childKindOf(kind)

        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'view',
                  icon: <Eye size={14} />,
                  label: t('organization.actions.viewDetails'),
                  onClick: () => onSelect(row)
                },
                ...(onEdit
                  ? [
                      {
                        key: 'edit',
                        icon: <Pencil size={14} />,
                        label: t('organization.actions.edit'),
                        onClick: () => onEdit(row)
                      }
                    ]
                  : []),
                ...(childKind && onAddChild
                  ? [
                      {
                        key: 'addChild',
                        icon: <Plus size={14} />,
                        label: t(`organization.actions.add.${childKind}`),
                        onClick: () => onAddChild(row)
                      }
                    ]
                  : []),
                ...(onDelete
                  ? [
                      { type: 'divider' as const },
                      {
                        key: 'delete',
                        icon: <Trash2 size={14} />,
                        label: t('organization.actions.delete'),
                        danger: true,
                        onClick: () => onDelete(row)
                      }
                    ]
                  : [])
              ]
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreHorizontal size={14} />}
              aria-label={t('organization.actions.more')}
            />
          </Dropdown>
        )
      }
    }
  ]

  return (
    <ProTableContainer<OrgUnit>
      options={false}
      showSearch={false}
      rowKey="id"
      dataSource={rows}
      columns={columns}
      pagination={{
        pageSize: DEFAULT_PER_PAGE,
        size: 'small',
        showTotal: (total) => t('organization.browse.total', { count: total })
      }}
    />
  )
}
